import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openFeedback } from "@/components/FeedbackModal";
import { toast } from "@/lib/toast";
import { ShieldCheck, Check, X, ArrowRight, Camera, Loader2, Lock } from "lucide-react";

// Reusable ID-verification entry flow. Two intro variants (standard / beta) -> pre-flight ->
// hand off to Stripe's hosted session. Copy is approved/verbatim. `next` is the app path the
// user should land back on after Stripe (carried through /verify/complete?next=).
export default function VerifyIdFlow({ open, onClose, next = "", token = "" }) {
  const { user } = useAuth();
  const isBeta = !!(user && user.is_beta_tester);
  const [step, setStep] = useState("intro"); // intro | preflight
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setStep("intro"); setError(""); } }, [open]);
  if (!open) return null;

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/identity/session", { next: next || window.location.pathname, token: token || undefined });
      window.location.href = data.url;
    } catch (err) {
      // The backend returns a readable 4xx/503 JSON body for every anticipated failure
      // (a bare 5xx would be rewritten to HTML by the proxy). Show it inline with a way to
      // report the problem, so the flow never dead-ends on a blank screen. A 409 mismatch
      // detail is an object — read its message directly.
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message)
        ? detail.message
        : (formatError(detail) || "ID verification is temporarily unavailable. Please try again shortly.");
      setError(msg);
      toast.error(msg);
      setBusy(false);
    }
  };

  const Checklist = ({ items }) => (
    <ul className="mt-4 space-y-2.5">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2.5 text-sm font-body text-foreground">
          <Check className="w-4 h-4 text-volt shrink-0 mt-0.5" strokeWidth={3} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );

  const Inset = ({ header, children }) => (
    <div className="mt-5 bg-obsidian border border-sniper p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">{header}</p>
      <p className="text-[13px] font-body text-secondary-foreground leading-relaxed">{children}</p>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto"
         data-testid="verify-id-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div className="relative w-full max-w-md bg-surface border border-sniper shadow-2xl my-auto">
        <button onClick={onClose} disabled={busy} data-testid="verify-modal-close"
          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-7">
          {step === "intro" && (isBeta ? (
            /* ---------- BETA VARIANT ---------- */
            <div data-testid="verify-intro-beta">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt">BETA · FOUNDING MEMBERS</p>
              <h2 className="font-heading text-2xl sm:text-[26px] leading-tight uppercase tracking-tight mt-2 text-foreground">
                Help us test ID verification
              </h2>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">
                You're one of the first people on OpticSwap, so you get to break this before real sellers touch it.
                Run the check and tell us anything that felt slow, confusing, or sketchy.
              </p>
              <Checklist items={[
                "Your ID VERIFIED badge carries over to launch",
                "Free — verification costs you nothing, ever",
                "Stripe sees the document, we never do",
              ]} />
              <Inset header="WHY NOW">
                We're running live payment tests this week. Verified beta accounts are what let us test payment
                release with real money before launch.
              </Inset>
              <button onClick={() => setStep("preflight")} data-testid="verify-run-check-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
                Run the check <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => { openFeedback({ category: "Other", prefill: "[Verification] " }); onClose?.(); }} data-testid="verify-report-problem-btn"
                className="mt-2 w-full text-center font-mono uppercase text-[11px] tracking-wide text-muted-foreground hover:text-foreground py-2 transition-colors">
                Report a problem instead
              </button>
              <a href="/faq#verification" target="_blank" rel="noopener noreferrer" data-testid="verify-how-it-works-link"
                className="mt-1 block w-full text-center font-mono uppercase text-[11px] tracking-wide text-olive-bright hover:text-volt py-1 transition-colors">
                How verification works
              </a>
            </div>
          ) : (
            /* ---------- STANDARD PRIMARY MODAL ---------- */
            <div data-testid="verify-intro-standard">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-olive-bright">TRUST &amp; SAFETY</p>
              <h2 className="font-heading text-2xl sm:text-[26px] leading-tight uppercase tracking-tight mt-2 text-foreground">
                Verify your ID to sell on OpticSwap
              </h2>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">
                Optics are a high-value, high-fraud category. Verification is what keeps ghost sellers and stolen
                gear off this marketplace — and it's why buyers here move faster than on the forums.
              </p>
              <Checklist items={[
                "Verified badge on your profile and every listing",
                "List for sale and receive payouts — required before your first sale settles",
                "Higher placement in search than unverified sellers",
                "Takes about two minutes on your phone",
              ]} />
              <Inset header="WHAT WE ACTUALLY SEE">
                Your ID goes directly to Stripe, our payments provider. OpticSwap never receives or stores the
                image of your document. We're told pass or fail, your legal name, and nothing else.
              </Inset>
              <button onClick={() => setStep("preflight")} data-testid="verify-my-id-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
                Verify my ID <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={onClose} data-testid="verify-maybe-later-btn"
                className="mt-2 w-full text-center font-mono uppercase text-[11px] tracking-wide text-muted-foreground hover:text-foreground py-2 transition-colors">
                Maybe later
              </button>
              <p className="mt-3 text-center text-[11px] font-body text-muted-foreground">
                Government photo ID + a selfie.
              </p>
              <a href="/faq#verification" target="_blank" rel="noopener noreferrer" data-testid="verify-how-it-works-link"
                className="mt-2 block w-full text-center font-mono uppercase text-[11px] tracking-wide text-olive-bright hover:text-volt py-1 transition-colors">
                How verification works
              </a>
            </div>
          ))}

          {step === "preflight" && (
            /* ---------- PRE-FLIGHT ---------- */
            <div data-testid="verify-preflight">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-olive-bright">STEP 2 OF 3</p>
              <div className="mt-3 flex items-center gap-2" data-testid="verify-progress">
                {[["Account", "done"], ["Identity", "current"], ["Payouts", "upcoming"]].map(([label, state]) => (
                  <div key={label} className="flex-1">
                    <div className={`h-1 ${state === "done" ? "bg-volt" : state === "current" ? "bg-olive-bright" : "bg-sniper"}`} />
                    <p className={`mt-1 font-mono text-[9px] uppercase tracking-wide ${state === "upcoming" ? "text-muted-foreground" : "text-foreground"}`}>{label}</p>
                  </div>
                ))}
              </div>
              <h2 className="font-heading text-2xl uppercase tracking-tight mt-5 text-foreground flex items-center gap-2">
                <Camera className="w-6 h-6 text-olive-bright" /> Have your ID ready
              </h2>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">
                You'll photograph the front and back of a government-issued ID, then take a selfie so Stripe can
                match your face to the document.
              </p>
              <Checklist items={[
                "Driver's license, state ID, or passport",
                "Photos must be taken with your camera — uploads are disabled",
                "Good light, no glare, all four corners in frame",
              ]} />
              <Inset header="HEADS UP">
                You get 3 attempts. If all three fail, an admin reviews it manually — you won't be locked out.
              </Inset>
              <button onClick={start} disabled={busy} data-testid="verify-continue-stripe-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <>Continue to Stripe <ArrowRight className="w-4 h-4" /></>}
              </button>
              {error && (
                <div className="mt-4 border border-blaze/50 bg-blaze/10 p-3.5" data-testid="verify-error">
                  <p className="text-[13px] font-body text-secondary-foreground leading-relaxed">{error}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button onClick={start} disabled={busy} data-testid="verify-retry-btn"
                      className="font-mono uppercase text-[11px] tracking-wide text-olive-bright hover:text-volt py-1.5 px-2 transition-colors disabled:opacity-50">
                      Try again
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button onClick={() => { openFeedback({ category: "Other", prefill: "[Verification] " }); onClose?.(); }} data-testid="verify-error-report-btn"
                      className="font-mono uppercase text-[11px] tracking-wide text-muted-foreground hover:text-foreground py-1.5 px-2 transition-colors">
                      Report a problem
                    </button>
                  </div>
                </div>
              )}
              <p className="mt-3 text-center text-[11px] font-body text-muted-foreground">
                You'll come back here automatically when you're done.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
